import React from 'react';
import { Alert, Box, Skeleton, Typography } from '@mui/material';

import { InfoTooltip } from '../../../../components/core';
import {
  OrderAttributionCurrency,
  OrderAttributionMetricsResponse,
} from '../../../../store/modules/merchant/types';
import { formatCount } from './formatMetrics';

type OrderAttributionPanelProps = {
  data?: OrderAttributionMetricsResponse;
  isLoading: boolean;
  errorMessage: string | null;
};

const CLASSES = [
  {
    key: 'direct',
    label: 'Direct',
    tip: 'Orders placed in the agent chat.',
  },
  {
    key: 'attributed',
    label: 'Attributed',
    tip: 'Orders where the agent helped the shopper reach checkout.',
  },
  {
    key: 'assisted',
    label: 'Assisted',
    tip: 'Orders where the agent helped build the cart.',
  },
] as const;

export const OrderAttributionPanel = ({
  data,
  isLoading,
  errorMessage,
}: OrderAttributionPanelProps) => {
  const currencies = data?.currencies.filter(
    (currency) =>
      !isUnknownCurrency(currency.currency) ||
      CLASSES.some(({ key }) => currency[key].orders > 0 || Number(currency[key].revenue) !== 0),
  );

  return (
    <Box
      component="section"
      aria-label="Order attribution"
      data-testid="ella-order-attribution"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: 1.5,
        border: '1px solid',
        borderColor: 'secondary.main',
        borderRadius: '8px',
        backgroundColor: 'background.paper',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography
          variant="medium"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', lineHeight: '18px' }}
        >
          Order attribution
        </Typography>
        <Typography
          variant="small"
          color="text.secondary"
          component="p"
          sx={{ lineHeight: '16px' }}
        >
          Paid orders linked to agent conversations.
        </Typography>
      </Box>

      {errorMessage ? (
        <Alert severity="error">{errorMessage}</Alert>
      ) : isLoading ? (
        <Box
          role="status"
          aria-label="Loading order attribution"
          sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
        >
          <Skeleton variant="text" width="55%" height={18} />
          <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
        </Box>
      ) : !data?.hasData ? (
        <Typography
          variant="small"
          color="text.secondary"
          data-testid="ella-order-attribution-empty"
        >
          No agent conversations in this date range.
        </Typography>
      ) : currencies?.length ? (
        currencies.map((currency) => <CurrencyBlock key={currency.currency} currency={currency} />)
      ) : (
        <Typography variant="small" color="text.secondary">
          No paid orders linked to agent conversations in this date range.
        </Typography>
      )}
    </Box>
  );
};

const CurrencyBlock = ({ currency }: { currency: OrderAttributionCurrency }) => {
  const rows = CLASSES.map((item) => ({ ...item, ...currency[item.key] }));
  const attributedRevenue = Number(currency.conciergeRevenue) || 1;
  const orderLabel = `${formatCount(currency.conciergeOrders)} ${currency.conciergeOrders === 1 ? 'order' : 'orders'}`;
  const average =
    currency.averageOrderValue === null
      ? '\u2014'
      : formatMoney(currency.averageOrderValue, currency.currency);

  return (
    <Box
      data-testid={`ella-order-attribution-${currency.currency.toLowerCase()}`}
      sx={{
        '& + &': { mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="small" color="text.secondary">
          Total attributed revenue
        </Typography>
        <Typography
          variant="small"
          color="text.secondary"
          sx={{
            px: 0.75,
            py: 0.25,
            border: '1px solid',
            borderColor: 'secondary.main',
            borderRadius: 1,
          }}
        >
          {isUnknownCurrency(currency.currency) ? 'Unknown currency' : currency.currency}
        </Typography>
      </Box>
      <Typography variant="huge" fontWeight="semibold" sx={{ letterSpacing: '-1.5px' }}>
        {formatMoney(currency.conciergeRevenue, currency.currency)}
      </Typography>
      <Typography variant="small" color="text.secondary" component="p" sx={{ mt: 0.5, mb: 2 }}>
        {orderLabel} {'\u00b7'} Avg. order {average}
      </Typography>
      {rows.map((item) => (
        <Box
          key={item.key}
          data-testid={`ella-attribution-${currency.currency.toLowerCase()}-${item.key}`}
          sx={{
            display: 'grid',
            gridTemplateColumns:
              item.key === 'assisted' ? '1fr auto' : '92px minmax(24px, 1fr) auto',
            gap: 1.5,
            alignItems: 'center',
            py: 1,
            ...(item.key === 'assisted' && {
              mt: 2,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
            }),
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="small">{item.label}</Typography>
              <InfoTooltip
                title={item.tip}
                placement="top"
                ariaLabel={`More information about ${item.label}`}
              />
            </Box>
            {item.key === 'assisted' && (
              <Typography variant="small" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
                Not included above
              </Typography>
            )}
          </Box>
          {item.key !== 'assisted' && (
            <Box sx={{ height: 6, borderRadius: 4, backgroundColor: '#EEE' }}>
              <Box
                sx={{
                  height: 6,
                  borderRadius: 4,
                  width: `${(Number(item.revenue) / attributedRevenue) * 100}%`,
                  backgroundColor: '#1E1E1E',
                }}
              />
            </Box>
          )}
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              variant={item.key === 'assisted' ? 'medium' : 'small'}
              fontWeight="semibold"
            >
              {formatMoney(item.revenue, currency.currency)}
            </Typography>
            <Typography variant="small" color="text.secondary" component="p">
              {formatCount(item.orders)} {item.orders === 1 ? 'order' : 'orders'}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const isUnknownCurrency = (currency: string): boolean => currency === '' || currency === 'XXX';

const formatMoney = (amount: string, currency: string): string => {
  if (isUnknownCurrency(currency)) {
    return amount;
  }
  if (!/^[A-Z]{3}$/.test(currency)) {
    return currency ? `${amount} ${currency}` : amount;
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(amount));
};
